import { BRAND_CONFIG_TIMEOUT_MS, readBrandJson } from './readBrandJson'
import {
  brandConfigPath,
  clearSavedBrand,
  loadSharedBrands,
  readSavedBrand,
  saveBrand,
  type SharedBrand
} from './sharedHost'
import type { PublishedConfig } from './types'

export { BRAND_CONFIG_TIMEOUT_MS }

// Each app's index.html preloads this exact path.
export const BRAND_CONFIG_PATH = '/brand/config.json'

export const SUPPORTED_SCHEMA_MAJOR = 1

export class UnsupportedSchemaVersionError extends Error {
  constructor(readonly schemaVersion: unknown) {
    super(`Unsupported brand config schemaVersion: ${String(schemaVersion)}`)
    this.name = 'UnsupportedSchemaVersionError'
  }
}

export class InvalidBrandConfigError extends Error {
  constructor(readonly field: string) {
    super(`Brand config is missing required field: ${field}`)
    this.name = 'InvalidBrandConfigError'
  }
}

export class WrongAppConfigError extends Error {
  constructor(
    readonly expected: string,
    readonly received: unknown
  ) {
    super(`Brand config is for app "${String(received)}", expected "${expected}"`)
    this.name = 'WrongAppConfigError'
  }
}

export class BrandConfigUnavailableError extends Error {
  constructor(readonly hostname: string) {
    super(`No brand config is published for ${hostname} and it has no legacy entry`)
    this.name = 'BrandConfigUnavailableError'
  }
}

const schemaMajor = (schemaVersion: unknown): number => parseInt(String(schemaVersion), 10)

const assertRequired = (doc: Partial<PublishedConfig>): void => {
  if (!doc.clientId) throw new InvalidBrandConfigError('clientId')
  if (!doc.appKey) throw new InvalidBrandConfigError('appKey')
  if (!doc.api?.baseUrl) throw new InvalidBrandConfigError('api.baseUrl')
  if (!doc.brand?.name) throw new InvalidBrandConfigError('brand.name')
}

interface LoadOptions {
  timeoutMs?: number
  app?: string
  path?: string
}

// null when nothing is published; rejects a config this build cannot use.
export const loadPublishedConfig = async <TApp = object>({
  timeoutMs = BRAND_CONFIG_TIMEOUT_MS,
  app,
  path = BRAND_CONFIG_PATH
}: LoadOptions = {}): Promise<PublishedConfig<TApp> | null> => {
  const doc = (await readBrandJson(path, timeoutMs)) as Partial<PublishedConfig<TApp>> | null
  if (!doc || typeof doc !== 'object' || doc.schemaVersion === undefined) return null

  if (schemaMajor(doc.schemaVersion) !== SUPPORTED_SCHEMA_MAJOR) {
    throw new UnsupportedSchemaVersionError(doc.schemaVersion)
  }
  if (app !== undefined && doc.app !== app) {
    throw new WrongAppConfigError(app, doc.app)
  }
  assertRequired(doc)

  return doc as PublishedConfig<TApp>
}

export interface LoadBrandConfigurationOptions<TApp, TConfig> extends Omit<LoadOptions, 'path'> {
  app: string
  fromPublished: (cfg: PublishedConfig<TApp>) => TConfig
  // null for a host the legacy table does not know: its default would be the wrong tenant.
  fallback: () => TConfig | null
  // Runs when a shared host boots a brand other than the saved one: end its session.
  onBrandChange?: () => void
}

export interface SharedBrandState {
  brands: SharedBrand[]
  slug: string
}

export interface BrandConfigurationResult<TConfig> {
  config: TConfig
  source: 'pinned' | 'shared' | 'legacy'
  shared?: SharedBrandState
}

const currentHostname = () =>
  typeof window !== 'undefined' ? window.location.hostname : 'unknown host'

// Pinned host: /brand/config.json. Shared host: /brand/brands.json, then the chosen brand's config.
export const loadBrandConfiguration = async <TApp, TConfig>({
  fromPublished,
  fallback,
  onBrandChange,
  ...options
}: LoadBrandConfigurationOptions<TApp, TConfig>): Promise<BrandConfigurationResult<TConfig>> => {
  const published = await loadPublishedConfig<TApp>(options)
  if (published) return { config: fromPublished(published), source: 'pinned' }

  const list = await loadSharedBrands(options.timeoutMs)
  if (list.kind === 'failed') throw new BrandConfigUnavailableError(currentHostname())
  if (list.kind === 'shared') {
    const { brands } = list
    const saved = readSavedBrand()
    // No picker (decision 2026-10-05): the saved brand while listed, otherwise the first.
    const slug = saved && brands.some((b) => b.slug === saved) ? saved : brands[0].slug

    let config: TConfig
    try {
      const picked = await loadPublishedConfig<TApp>({ ...options, path: brandConfigPath(slug) })
      if (!picked) throw new BrandConfigUnavailableError(currentHostname())
      config = fromPublished(picked)
    } catch (error) {
      // Forgotten, so a broken brand never traps the device: the next try boots the first one.
      clearSavedBrand()
      throw error
    }

    saveBrand(slug)
    // Only when the pick is remembered: without storage every boot would end the session.
    if (slug !== saved && readSavedBrand() === slug) onBrandChange?.()
    return { config, source: 'shared', shared: { brands, slug } }
  }

  const legacy = fallback()
  if (legacy === null) throw new BrandConfigUnavailableError(currentHostname())
  return { config: legacy, source: 'legacy' }
}
